// The Evals guide's sections (TOM-139): every anchor the guide page renders,
// the link to one, and the experts' names as the guide gives them. Each
// page's "How this page works" link (GuideLink) names one of these anchors,
// and guide.test.ts checks that every one it names is rendered.

export const GUIDE_PATH = '/admin/evals/guide';

/** Every anchor on the guide, in page order. */
export const GUIDE_ANCHORS = [
  // What this section is for
  'purpose',
  'change-neo',
  'models',
  // The words
  'words',
  'case',
  'run',
  'run-page',
  'regrade',
  'launch',
  'splits',
  'labels',
  'agreement',
  'cases-inbox',
  'case-draft',
  'case-agent',
  'consents',
  'compare',
  'overview',
  'weekly',
  // Fixing one bad answer
  'fix-one-answer',
  'fix-find',
  'fix-mark',
  'fix-write',
  'fix-change',
  'fix-check',
  // Working on one expert
  'experts',
  'experts-list',
  'expert-parts',
  'teach-expert',
  'boat-notebook',
  'experts-console',
  'expert-runs',
  'experts-command-line',
] as const;

export type GuideAnchor = (typeof GUIDE_ANCHORS)[number];

export function isGuideAnchor(value: string): value is GuideAnchor {
  return (GUIDE_ANCHORS as readonly string[]).includes(value);
}

export function guideHref(anchor: GuideAnchor): string {
  return `${GUIDE_PATH}#${anchor}`;
}

/** The guide's top-level sections, for its contents list. */
export const GUIDE_SECTIONS: ReadonlyArray<{ id: GuideAnchor; title: string }> = [
  { id: 'purpose', title: 'What this section is for' },
  { id: 'words', title: 'The words' },
  { id: 'fix-one-answer', title: 'Fixing one bad answer' },
  { id: 'experts', title: 'Working on one expert' },
];

/**
 * How the guide names each expert and what it does, by its id in the server's
 * registry (cloud/src/core/neo/specialists/registry.ts). Which ones owners get
 * is not here: the guide reads it live from GET /v1/admin/models.
 */
export const EXPERT_TEXT: Readonly<Record<string, { name: string; what: string }>> = {
  boards: {
    name: 'Board editor',
    what: "Makes and changes the owner's boards. In a chat, Neo hands it the board changes an owner asks for. In a board's Edit chat, Neo works under its rules with its tools itself.",
  },
  electrical: {
    name: 'Electrical (Sparky)',
    what: "Reads the boat's batteries and charging. When Jev spots a battery or charging question, Sparky's rules, notes and power report go straight into Neo's own answer; otherwise Neo can ask him.",
  },
  mechanical: {
    name: 'Mechanical',
    what: "Reads the boat's engines.",
  },
  electronics: {
    name: 'Electronics',
    what: "Reads the boat's instrument network and the devices on it.",
  },
  navigation: {
    name: 'Navigator',
    what: 'Reads where the boat is and where it has been: its legs and stops, its mode and anchor watch, its route, the weather and tides, and the vessels around it.',
  },
};

/** An expert's name for the guide; an id the guide has no words for reads as itself. */
export function expertName(id: string): string {
  return EXPERT_TEXT[id]?.name ?? id.charAt(0).toUpperCase() + id.slice(1);
}
